import React, { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Archive, ArrowClockwise, Buildings, Export, FileCsv, UploadSimple, FilePdf, FileXls, MagnifyingGlass, Plus, Table, UserCircle, WarningCircle,
} from '@phosphor-icons/react';
import { useIslandActions } from '../../../../components/island/IslandProvider';
import { useNotification } from '../../../../contexts/NotificationContext';
import { BestaetigenDialog, ImmoButton, MenueEintrag, Popover } from '../../../../components/immobilien/ui';
import { heuteIso } from '../../../../lib/immobilien/format';
import {
  baueObjektZeilen, filtereObjekte, leerstandListe, letzteMonate, portfolio, sollKaltmieteJeMonat, sortiereNachNummer,
  zustaendigkeiten, type ObjektZeile, type VaFilter,
} from '../../../../lib/immobilien/portfolio';
import { loescheObjekt, setzeZustaendig, useObjektListe, type ZustaendigOption } from '../../../../lib/immobilien/useObjektListe';
import { mitViewTransition } from '../../../../lib/immobilien/viewTransition';
import { ARCHIV_STATUS, istVerknuepfungsFehler, setzeArchiviert } from '../../../../lib/immobilien/archiv';
import { exportCsv } from '../../../../lib/export/exportCsv';
import { exportXlsx } from '../../../../lib/export/exportXlsx';
import { exportPdfReport, exportPdfTable } from '../../../../lib/export/exportPdf';
import type { ExportColumn } from '../../../../lib/export/types';
import NeuesObjektFormular from '../NeuesObjektFormular';
import BelegungImportFormular from '../BelegungImportFormular';
import ObjektEditSidebar from '../ObjektEditSidebar';
import type { Objekt } from '../ObjekteListe';
import { PortfolioWidgets } from './PortfolioWidgets';
import { ObjektKachel } from './ObjektKachel';
import { ObjektTabelle } from './ObjektTabelle';

/** Spalten des Objekt-Exports – unverändert aus der bisherigen Liste. */
const OBJEKTE_EXPORT_COLUMNS: ExportColumn[] = [
  { key: 'bezeichnung', label: 'Bezeichnung' },
  { key: 'strasse', label: 'Straße' },
  { key: 'hausnummer', label: 'Hausnummer' },
  { key: 'plz', label: 'PLZ' },
  { key: 'stadt', label: 'Stadt' },
  { key: 'objekttyp', label: 'Objekttyp' },
  { key: 'status', label: 'Status' },
  { key: 'baujahr', label: 'Baujahr' },
  { key: 'wohnflaeche', label: 'Wohnfläche', format: (v) => v ? `${v} m²` : '' },
];

type Ansicht = 'kacheln' | 'liste';
const ANSICHT_SCHLUESSEL = 'immo-listenansicht';
const leseAnsicht = (): Ansicht => {
  try { return localStorage.getItem(ANSICHT_SCHLUESSEL) === 'liste' ? 'liste' : 'kacheln'; } catch { return 'kacheln'; }
};

const VA_FILTER: { key: VaFilter; label: string }[] = [
  { key: 'alle', label: 'Alle' }, { key: 'MV', label: 'MV' }, { key: 'WEG', label: 'WEG' }, { key: 'SEV', label: 'SEV' },
];

/** Kopf-Knopf laut Prototyp: 36 px, Fläche --fill2 mit Linie, 13/500. */
function KopfKnopf({ icon: IconKomp, children, ...rest }: React.ComponentPropsWithRef<'button'> & { icon: typeof Table }) {
  return (
    <button type="button" {...rest} className="immo-hover-flaeche flex items-center cursor-pointer"
      style={{ height: 36, padding: '0 15px', borderRadius: 18, border: 'none', background: 'var(--fill2)', boxShadow: 'inset 0 0 0 1px var(--line)', gap: 7, fontSize: 13, fontWeight: 500, color: 'var(--ink)' }}>
      <IconKomp size={15} />
      {children}
    </button>
  );
}

function FilterChip({ an, onClick, children, person = false, titel }: { an: boolean; onClick: () => void; children: React.ReactNode; person?: boolean; titel?: string }) {
  return (
    <button type="button" aria-pressed={an} onClick={onClick} title={titel ?? (person ? `Zuständig: ${String(children)}` : `Verwaltungsart: ${String(children)}`)}
      className="flex items-center shrink-0 whitespace-nowrap cursor-pointer"
      style={{ gap: 5, height: 34, padding: '0 13px', borderRadius: 17, border: 'none', fontSize: 12, fontWeight: 600, background: an ? '#fff' : 'var(--fill2)', color: an ? '#111' : 'var(--ink)' }}>
      {person && <UserCircle size={14} aria-hidden />}
      {children}
    </button>
  );
}

export default function ObjektListeSeite() {
  const navigate = useNavigate();
  const { openForm, closeForm } = useIslandActions();
  const { showToast } = useNotification();
  const { zustand, neuLaden, setzeObjekt } = useObjektListe();

  const [suche, setSuche] = useState('');
  const [va, setVa] = useState<VaFilter>('alle');
  const [zustaendigFilter, setZustaendigFilter] = useState<string | null>(null);
  const [ansicht, setAnsichtState] = useState<Ansicht>(leseAnsicht);
  const [aendertId, setAendertId] = useState<string | null>(null);
  const [bearbeiten, setBearbeiten] = useState<Objekt | null>(null);
  const [loeschen, setLoeschen] = useState<ObjektZeile | null>(null);
  const [loeschtGerade, setLoeschtGerade] = useState(false);
  const [exportiert, setExportiert] = useState(false);
  const [archiv, setArchiv] = useState(false);

  const setAnsicht = (a: Ansicht) => {
    setAnsichtState(a);
    try { localStorage.setItem(ANSICHT_SCHLUESSEL, a); } catch { /* nur für diese Sitzung */ }
  };

  const daten = zustand.status === 'ok' ? zustand.daten : null;
  const heute = heuteIso();
  const monate = useMemo(() => letzteMonate(heute, 12), [heute]);

  const alle = useMemo(
    () => (daten ? sortiereNachNummer(baueObjektZeilen(daten.objekte, daten.einheiten, daten.mieter, daten.sevEinheiten)) : []),
    [daten],
  );
  const sichtbar = useMemo(() => filtereObjekte(alle, { suche, va, zustaendig: zustaendigFilter, archiv }), [alle, suche, va, zustaendigFilter, archiv]);
  // Portfolio-Kennzahlen ohne archivierte Objekte
  const aktive = useMemo(() => alle.filter(z => !z.archiviert), [alle]);
  const archivAnzahl = alle.length - aktive.length;
  const kennzahlen = useMemo(() => portfolio(aktive), [aktive]);
  const sollVerlauf = useMemo(() => {
    if (!daten) return [];
    const ids = new Set(aktive.map(z => z.id));
    return sollKaltmieteJeMonat(daten.mieter.filter(m => m.objekt_id !== null && ids.has(m.objekt_id)), monate);
  }, [daten, aktive, monate]);
  const zustaendigListe = useMemo(() => zustaendigkeiten(aktive, 5), [aktive]);
  const zustaendigChips = useMemo(() => zustaendigkeiten(aktive, 50).map(z => z.name), [aktive]);

  const oeffne = useCallback((id: string) => {
    mitViewTransition(() => {
      navigate(`/admin/objekte/${encodeURIComponent(id)}`);
      window.scrollTo(0, 0);
    });
  }, [navigate]);

  const neuesObjekt = () => openForm(<NeuesObjektFormular onDone={() => { closeForm(); neuLaden(); }} />, 'Neues Objekt', undefined,
    { icon: Buildings, farbe: 'blau', badge: 'plus', pflichtHinweis: true });
  const belegung = () => openForm(<BelegungImportFormular onDone={() => { closeForm(); neuLaden(); }} />, 'Belegungslisten importieren', 700,
    { icon: UploadSimple, farbe: 'gruen', badge: 'csv' });

  const exportiere = async (format: 'csv' | 'xlsx' | 'pdf-table' | 'pdf-report') => {
    if (!daten) return;
    setExportiert(true);
    try {
      const istAktiv = (s: string | null | undefined) => (s ?? '').toLowerCase() === 'aktiv';
      const config = {
        title: 'Objekte',
        columns: OBJEKTE_EXPORT_COLUMNS,
        data: daten.objekte as unknown as Record<string, unknown>[],
        stats: { items: [
          { label: 'Gesamt', value: daten.objekte.length },
          { label: 'Aktiv', value: daten.objekte.filter(o => istAktiv(o.status)).length },
          { label: 'Inaktiv', value: daten.objekte.filter(o => !istAktiv(o.status)).length },
        ] },
      };
      if (format === 'csv') exportCsv(config);
      else if (format === 'xlsx') exportXlsx(config);
      else if (format === 'pdf-table') exportPdfTable(config);
      else exportPdfReport(config);
    } catch (e) {
      showToast({ type: 'error', message: `Export fehlgeschlagen: ${e instanceof Error ? e.message : 'Unbekannter Fehler'}` });
    } finally {
      setExportiert(false);
    }
  };

  const aendereZustaendig = async (z: ObjektZeile, ziel: ZustaendigOption | null) => {
    setAendertId(z.id);
    const vorher = { zustaendig: z.roh.zustaendig ?? null, zustaendig_id: z.roh.zustaendig_id ?? null, zustaendig_typ: z.roh.zustaendig_typ ?? null };
    setzeObjekt(z.id, { zustaendig: ziel ? ziel.name : 'Global', zustaendig_id: ziel?.id ?? null, zustaendig_typ: ziel?.typ ?? null });
    try {
      await setzeZustaendig(z.id, ziel);
      showToast({ type: 'success', message: `Zuständig für ${z.name}: ${ziel ? ziel.name : 'Global'}` });
    } catch (e) {
      setzeObjekt(z.id, vorher);
      showToast({ type: 'error', message: `Zuständigkeit nicht gespeichert: ${e instanceof Error ? e.message : 'Unbekannter Fehler'}` });
    } finally {
      setAendertId(null);
    }
  };

  const bestaetigeLoeschen = async () => {
    if (!loeschen) return;
    setLoeschtGerade(true);
    try {
      await loescheObjekt(loeschen.id);
      showToast({ type: 'success', message: `${loeschen.name} gelöscht` });
      setLoeschen(null);
      neuLaden();
    } catch (e) {
      const meldung = e instanceof Error ? e.message : 'Unbekannter Fehler';
      showToast({ type: 'error', message: istVerknuepfungsFehler(meldung)
        ? `${loeschen.name} hat verknüpfte Daten und kann nicht gelöscht werden. Archiviere das Objekt stattdessen (Menü „···“ → Archivieren).`
        : `Fehler beim Löschen des Objekts: ${meldung}` });
    } finally {
      setLoeschtGerade(false);
    }
  };

  const filterAktiv = suche.trim() !== '' || va !== 'alle' || zustaendigFilter !== null || archiv;

  const archivieren = async (z: ObjektZeile, an: boolean) => {
    setAendertId(z.id);
    try {
      await setzeArchiviert(z.id, an);
      setzeObjekt(z.id, { status: an ? ARCHIV_STATUS : 'Aktiv' });
      showToast({ type: 'success', message: an ? `${z.name} archiviert` : `${z.name} wiederhergestellt` });
    } catch (e) {
      showToast({ type: 'error', message: `Nicht gespeichert: ${e instanceof Error ? e.message : 'Unbekannter Fehler'}` });
    } finally {
      setAendertId(null);
    }
  };
  const datum = new Date().toLocaleDateString('de-DE', { weekday: 'long', day: 'numeric', month: 'long' });

  return (
    <>
      <header className="flex items-end justify-between flex-wrap" style={{ gap: 16 }}>
        <div className="flex flex-col" style={{ gap: 2 }}>
          <span style={{ fontSize: 13, color: 'var(--ink2)', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{datum}</span>
          <h1 className="m-0" style={{ fontSize: 46, lineHeight: 1.05, fontWeight: 700, letterSpacing: '-0.035em' }}>Immobilien</h1>
        </div>
        <div className="flex items-center flex-wrap" style={{ gap: 8 }}>
          <KopfKnopf icon={Table} onClick={belegung}>Belegungslisten</KopfKnopf>
          <Popover
            label="Export"
            breite={200}
            ausloeser={({ offen, umschalten, ref }) => (
              <KopfKnopf icon={Export} ref={ref} aria-haspopup="menu" aria-expanded={offen} onClick={umschalten} disabled={!daten || exportiert}>
                {exportiert ? 'Exportiert …' : 'Export'}
              </KopfKnopf>
            )}
          >
            {schliessen => (
              <>
                <MenueEintrag onClick={() => { schliessen(); void exportiere('csv'); }}><FileCsv size={16} color="var(--ok)" />CSV</MenueEintrag>
                <MenueEintrag onClick={() => { schliessen(); void exportiere('xlsx'); }}><FileXls size={16} color="var(--info)" />Excel</MenueEintrag>
                <MenueEintrag onClick={() => { schliessen(); void exportiere('pdf-table'); }}><FilePdf size={16} color="var(--bad)" />PDF (Tabelle)</MenueEintrag>
                <MenueEintrag onClick={() => { schliessen(); void exportiere('pdf-report'); }}><FilePdf size={16} color="var(--hl)" />PDF (Bericht)</MenueEintrag>
              </>
            )}
          </Popover>
          <ImmoButton art="akzent" groesse={36} icon={Plus} onClick={neuesObjekt} style={{ padding: '0 17px', fontWeight: 600 }}>Neues Objekt</ImmoButton>
        </div>
      </header>

      {zustand.status === 'fehler' && (
        <div role="alert" className="immo-glas flex items-center flex-wrap" style={{ borderRadius: 26, padding: 20, gap: 14 }}>
          <WarningCircle weight="fill" size={28} color="var(--bad)" />
          <div className="flex flex-col flex-1" style={{ minWidth: 200 }}>
            <span style={{ fontSize: 15, fontWeight: 800 }}>Objekte konnten nicht geladen werden</span>
            <span style={{ fontSize: 12, color: 'var(--ink3)' }}>{zustand.meldung}</span>
          </div>
          <ImmoButton icon={ArrowClockwise} onClick={neuLaden}>Erneut versuchen</ImmoButton>
        </div>
      )}

      {daten && (
        <PortfolioWidgets
          kennzahlen={kennzahlen}
          sollVerlauf={sollVerlauf}
          monate={monate}
          tickets={daten.tickets}
          leerstand={leerstandListe(aktive, 4)}
          zustaendig={zustaendigListe}
          zustaendigFilter={zustaendigFilter}
          onZustaendig={setZustaendigFilter}
          onObjekt={oeffne}
        />
      )}

      <div className="immo-glas flex items-center justify-between" style={{ gap: 12, position: 'sticky', top: 12, zIndex: 5, padding: 8, borderRadius: 22 }}>
        <div className="immo-ohne-scrollbar flex items-center flex-1 min-w-0 overflow-x-auto" style={{ gap: 8 }}>
          <label className="flex items-center shrink-0" style={{ gap: 8, height: 34, padding: '0 12px', borderRadius: 17, background: 'var(--fill2)', width: 210 }}>
            <MagnifyingGlass size={15} color="var(--ink3)" aria-hidden />
            <input value={suche} onChange={e => setSuche(e.target.value)} placeholder="Objekt, Adresse, Nr." aria-label="Objekte durchsuchen"
              className="flex-1 min-w-0" style={{ border: 'none', background: 'transparent', outline: 'none', fontSize: 13, color: 'var(--ink)' }} />
          </label>
          {VA_FILTER.map(f => <FilterChip key={f.key} an={va === f.key} onClick={() => setVa(f.key)}>{f.label}</FilterChip>)}
          {zustaendigChips.length > 0 && <span aria-hidden className="shrink-0" style={{ width: 1, height: 20, background: 'var(--line)' }} />}
          {zustaendigChips.map(name => (
            <FilterChip key={name} person an={zustaendigFilter === name} onClick={() => setZustaendigFilter(zustaendigFilter === name ? null : name)}>{name}</FilterChip>
          ))}
          {archivAnzahl > 0 && (
            <>
              <span aria-hidden className="shrink-0" style={{ width: 1, height: 20, background: 'var(--line)' }} />
              <FilterChip an={archiv} onClick={() => setArchiv(a => !a)} titel={archiv ? 'Zurück zu den aktiven Objekten' : 'Archivierte Objekte zeigen'}>
                <Archive size={14} aria-hidden />Archiv · {archivAnzahl}
              </FilterChip>
            </>
          )}
        </div>
        <div role="tablist" aria-label="Ansicht" className="flex shrink-0" style={{ padding: 3, borderRadius: 17, background: 'var(--fill2)' }}>
          {([['kacheln', 'Kacheln'], ['liste', 'Liste']] as const).map(([k, l]) => (
            <button key={k} type="button" role="tab" aria-selected={ansicht === k} onClick={() => setAnsicht(k)} className="cursor-pointer"
              style={{ height: 28, padding: '0 14px', border: 'none', borderRadius: 14, fontSize: 12, fontWeight: 600, color: 'var(--ink)', background: ansicht === k ? 'var(--fill3)' : 'transparent' }}>
              {l}
            </button>
          ))}
        </div>
      </div>

      {zustand.status === 'laedt' && (
        <section aria-busy="true" aria-label="Objekte werden geladen" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 16 }}>
          {Array.from({ length: 6 }, (_, i) => (
            <div key={i} className="animate-pulse" style={{ height: 320, borderRadius: 30, background: 'var(--fill2)' }} />
          ))}
        </section>
      )}

      {daten && sichtbar.length === 0 && (
        <div className="flex flex-col items-center text-center" style={{ padding: '64px 20px', gap: 10, borderRadius: 28, background: 'var(--fill2)', boxShadow: 'inset 0 0 0 1px var(--line)' }}>
          <span style={{ fontSize: 18, fontWeight: 700 }}>Keine Objekte gefunden</span>
          <span style={{ fontSize: 13, color: 'var(--ink2)' }}>{alle.length === 0 ? 'Lege das erste Objekt an.' : 'Passe Suche oder Filter an.'}</span>
          {filterAktiv && (
            <ImmoButton onClick={() => { setSuche(''); setVa('alle'); setZustaendigFilter(null); setArchiv(false); }}>Filter zurücksetzen</ImmoButton>
          )}
        </div>
      )}

      {daten && sichtbar.length > 0 && ansicht === 'kacheln' && (
        <section aria-label="Objekte" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 16 }}>
          {sichtbar.map(z => <ObjektKachel key={z.id} z={z} onOeffnen={() => oeffne(z.id)} />)}
        </section>
      )}

      {daten && sichtbar.length > 0 && ansicht === 'liste' && (
        <ObjektTabelle
          zeilen={sichtbar}
          mitarbeiter={daten.mitarbeiter}
          teams={daten.teams}
          aendertId={aendertId}
          onOeffnen={z => oeffne(z.id)}
          onZustaendig={aendereZustaendig}
          onBearbeiten={z => setBearbeiten(z.roh as unknown as Objekt)}
          onLoeschen={setLoeschen}
          onArchivieren={archivieren}
        />
      )}

      {/* Bearbeiten: bestehende Seitenleiste (alle Felder wie bisher) */}
      <ObjektEditSidebar isOpen={bearbeiten !== null} objekt={bearbeiten} onClose={() => setBearbeiten(null)} onSuccess={() => { setBearbeiten(null); neuLaden(); }} />

      {loeschen && (
        <BestaetigenDialog
          titel="Objekt löschen?"
          text="Diese Aktion kann nicht rückgängig gemacht werden"
          detail={<><b style={{ fontWeight: 700 }}>{loeschen.name}</b><br /><span style={{ fontSize: 12, color: 'var(--ink3)' }}>{loeschen.adresse}</span></>}
          bestaetigenLabel="Löschen"
          laeuft={loeschtGerade}
          onBestaetigen={() => { void bestaetigeLoeschen(); }}
          onAbbrechen={() => setLoeschen(null)}
        />
      )}
    </>
  );
}
